import React, { useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { useMainPerformanceSatisfactionAnalysis, SectionProps } from '../../hooks/useMainPerformanceSections';
import { useSatisfactionAverage } from '../../hooks/useSatisfactionAverage';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import { getResponsiveXAxisTicks } from '../../utils/chartResponsive';
import ChartControls from '../ui/ChartControls';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import EmptyState from '../ui/EmptyState';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';

interface CustomerSatisfactionAnalysisProps extends SectionProps {
  branchId: number;
  timeRange?: string;
}

const CustomerSatisfactionAnalysis: React.FC<CustomerSatisfactionAnalysisProps> = ({
  branchId,
  timeRange = 'last6Months',
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: satisfactionData, isLoading, isError, error, refetch } = useMainPerformanceSatisfactionAnalysis(branchId, timeRange);
  const { data: averageData, isLoading: isLoadingAverage } = useSatisfactionAverage(branchId);
  
  // Component metadata
  const componentId = 'customer-satisfaction-analysis';
  const componentTitle = 'تحلیل رضایت مشتریان';

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  // Prepare satisfaction comparison data over time
  const prepareSatisfactionOverTime = () => {
    if (!satisfactionData?.satisfactionTrend || satisfactionData.satisfactionTrend.length === 0) {
      return {
        labels: [],
        branch: [],
        brand: [],
        all: []
      };
    }
    
    const months = satisfactionData.satisfactionTrend.map(item => item.month || '');
    const toChartValue = (value: number | null | undefined) =>
      value != null ? Number(value.toFixed(2)) : null;
    const branchData = satisfactionData.satisfactionTrend.map(item => toChartValue(item.branch));
    const brandData = satisfactionData.satisfactionTrend.map(item => toChartValue(item.brand));
    
    return {
      labels: months,
      branch: branchData,
      brand: brandData
    };
  };

  if (isLoading && showLoading) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'} animate-enter animate-delay-3`}>
        <div className="card-base card-padding">
          <div className="flex flex-col items-center justify-center h-64">
            <LoadingSpinner
              variant="minimal"
              size="lg"
              message="در حال بارگذاری تحلیل رضایت…"
            />
          </div>
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'}`}>
        <div className="card-base card-padding">
          <ErrorMessage 
            message="خطا در دریافت اطلاعات تحلیل رضایت"
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  if (!satisfactionData?.satisfactionTrend || satisfactionData.satisfactionTrend.length === 0) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'}`}>
        <div className="card-base card-padding">
          <EmptyState />
        </div>
      </div>
    );
  }

  // Get satisfaction data over time
  const satisfactionOverTime = prepareSatisfactionOverTime();

  // Use backend data for 12-month average
  const average12Months = averageData?.average12Months || 0;
  const lastMonthValue = averageData?.lastMonthValue || 0;
  const performanceTrend = averageData?.trend === 'better' ? 'بهتر' : averageData?.trend === 'worse' ? 'ضعیف‌تر' : 'ثابت';
  const trendColor = averageData?.trend === 'better' ? '#10b981' : averageData?.trend === 'worse' ? '#ef4444' : '#6b7280';
  
  // Calculate current status based on last month vs 12-month average
  const difference = lastMonthValue - average12Months;
  const getStatus = () => {
    if (difference > 2) return 'success';  // More than 2% above average
    if (difference < -2) return 'danger';  // More than 2% below average
    return 'info';  // Within ±2% tolerance
  };
  
  const status = getStatus();
  const statusMessages = {
    success: 'عملکرد بهتر از میانگین',
    danger: 'پایین‌تر از میانگین',
    info: 'در حد میانگین'
  };

  // Prepare satisfaction comparison data with changes
  const satisfactionComparisonData = {
    labels: satisfactionOverTime.labels,
    datasets: [
      {
        type: 'line' as const,
        label: 'شعبه فعلی',
        data: satisfactionOverTime.branch,
        spanGaps: false,
        borderColor: getChartColors(currentTheme)[0],
        backgroundColor: getChartBackgroundColor(0, currentTheme, 0.2),
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: getChartColors(currentTheme)[0],
        tension: 0.4,
        fill: false
      },
      {
        type: 'line' as const,
        label: 'میانگین برند',
        data: satisfactionOverTime.brand,
        spanGaps: false,
        borderColor: getChartColors(currentTheme)[1],
        backgroundColor: getChartColors(currentTheme)[1],
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: getChartColors(currentTheme)[1],
        tension: 0.4,
        fill: false,
      },
      {
        type: 'line' as const,
        label: 'میانگین ۱۲ ماه گذشته',
        data: Array(satisfactionOverTime.labels.length).fill(average12Months.toFixed(2)),
        borderColor: '#6366f1',
        backgroundColor: '#6366f1',        
        borderWidth: 2,
        borderDash: [10, 5],
        pointRadius: 0,
        fill: false
      }
    ]
  };

  return (
    <div ref={chartRef} className={`${compact ? 'col-span-1' : 'col-span-1'} card-base card-padding animate-enter animate-delay-3`}>
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="chart"
        dashboardSource="mainPerformance"
        onInfo={() => openGuide(CHART_GUIDES.customerSatisfactionAnalysis)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'mainPerformance',
          componentType: 'chart',
          description: 'تحلیل روند رضایت مشتریان در مقایسه با میانگین برند',
          props: { branchId, timeRange, compact },
          gridLayout: {
            containerClass: 'grid grid-cols-1 lg:grid-cols-2 gap-6',
            companions: ['monthly-sales-comparison']
          }
        })}
        isFavorited={isFavorited(componentId)}
        className="mb-4"
        chartRef={chartRef}
      />
      <h3 className="title-card">{componentTitle}</h3>
      <div className="mobile-chart-host" style={{ height: 400, width: '100%' }} role="img" aria-label="نمودار تحلیل رضایت مشتریان به تفکیک ماه">
        <Line
          data={satisfactionComparisonData}
          options={{
            ...getChartJsOptions(currentTheme),
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                  label: function(context: any) {
                    if (context.parsed.y === null) return '';
                    const label = context.dataset.label || '';
                    const value = context.parsed.y;
                    const separator = '\u00A0:\u00A0';
                    const formattedValue = `${value.toFixed ? value.toFixed(1) : value}%`;
                    return `${label}${separator}${formattedValue}`;
                  }
                }
              }
            },
            scales: {
              x: {
                ...getChartJsOptions(currentTheme).scales?.x,
                title: {   
                  display: true,
                  text: 'ماه',
                  color: 'var(--chart-axis-color)',
                },
                ticks: getResponsiveXAxisTicks(
                  getChartJsOptions(currentTheme).scales?.x?.ticks as Record<string, unknown> | undefined
                ),
              },
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                beginAtZero: false,
                min: 40,
                max: 105,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function(value: any) {
                    return `${value}%`;
                  }
                },
                title: {
                  display: true,
                  text: 'درصد رضایت',
                  color: 'var(--chart-axis-color)',
                },
              }
            },
          }}
        />
      </div>
      {isLoadingAverage ? (
        <div className="mt-4 text-sm text-text-tertiary">
          در حال محاسبه میانگین ۱۲ ماه…
        </div>
      ) : averageData ? (
        <div className={`
          mt-4 p-4 rounded-lg border-2 text-sm
          ${status === 'success' ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700' : ''}
          ${status === 'danger' ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700' : ''}
          ${status === 'info' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700' : ''}
        `}>
          {/* Status Header */}
          <div className={`flex items-center justify-between mb-3 pb-3 border-b
            ${status === 'success' ? 'border-green-200 dark:border-green-800' : ''}
            ${status === 'danger' ? 'border-red-200 dark:border-red-800' : ''}
            ${status === 'info' ? 'border-blue-200 dark:border-blue-800' : ''}
          `}>
            <div className={`text-lg font-bold
              ${status === 'success' ? 'text-green-800 dark:text-green-200' : ''}
              ${status === 'danger' ? 'text-red-800 dark:text-red-200' : ''}
              ${status === 'info' ? 'text-blue-800 dark:text-blue-200' : ''}
            `}>
              {statusMessages[status as keyof typeof statusMessages]}
            </div>
            <div dir="ltr" className={`text-2xl font-bold
              ${status === 'success' ? 'text-green-700 dark:text-green-300' : ''}
              ${status === 'danger' ? 'text-red-700 dark:text-red-300' : ''}
              ${status === 'info' ? 'text-blue-700 dark:text-blue-300' : ''}
            `}>
              {difference.toFixed(1)}%{difference > 0 ? '+' : ''}
            </div>
          </div>
          
          {/* Details Row */}
          <div className={`satisfaction-summary-details flex items-center justify-between text-sm
            ${status === 'success' ? 'text-green-700 dark:text-green-300' : ''}
            ${status === 'danger' ? 'text-red-700 dark:text-red-300' : ''}
            ${status === 'info' ? 'text-blue-700 dark:text-blue-300' : ''}
          `}>
            <div>
              <span className="opacity-80">ماه گذشته:</span>
              <span className="font-semibold mr-2">{lastMonthValue.toFixed(1)}%</span>
            </div>
            <div className="text-gray-400 dark:text-gray-500">|</div>
            <div>
              <span className="opacity-80">میانگین ۱۲ ماه:</span>
              <span className="font-semibold mr-2">{average12Months.toFixed(1)}%</span>
            </div>
            <div className="text-gray-400 dark:text-gray-500">|</div>
            <div>
              <span className="opacity-80">روند:</span>
              <span className="font-semibold mr-2">
                {performanceTrend}
                {/* {averageData.trendPercentage !== 0 && (
                  <span className="text-xs mr-1">
                    ({averageData.trendPercentage.toFixed(1)}%{difference > 0 ? '+' : ''})
                  </span>
                )} */}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CustomerSatisfactionAnalysis;